import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lms/shared-types';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { UserCustomFieldInput, type UserCustomFieldDefinition } from '@/components/users/UserCustomFieldInput';
import { usePermissions } from '@/hooks/usePermissions';
import { api, listAllResource, listResource } from '@/lib/api';
import { hierarchyLevelLabel, roleHierarchyLevel, roleLabel, sortRolesByHierarchy } from '@/lib/role-display';
import { parseCustomFieldMediaIds } from '@/lib/custom-field-display';
import { compareCustomFieldOrder, customFieldFormSpan, customFieldUsesFullWidth } from '@/lib/custom-field-form-order';
import {
  fallbackUserFormLayout,
  formColumnSpan,
  formGroupIcon,
  isUserFormBuiltin,
  layoutGroupsForExtra,
  layoutItemsForSection,
  type UserFormLayoutItem,
  type UserFormSectionId,
} from '@/lib/user-form-layout';
import { formatChileanPeso } from '@/lib/format-chilean-peso';
import { matchesSearch } from '@/lib/search-normalize';
import { userFormErrorToast, userFormSuccessToast } from '@/lib/user-feedback';
import { extractUserRoleSlugs } from '@/lib/user-roles';

interface UserForm {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  password?: string;
  passwordConfirmation?: string;
  status?: string;
  roles?: string[];
  customFields?: Record<string, string | string[] | boolean>;
  courseIds?: string[];
}

interface CourseOption {
  id: string;
  name: string;
  code?: string | null;
  status?: string;
}

interface CustomFieldValueRow {
  slug: string;
  type?: string;
  rawValue?: string | null;
}

interface UserDetail {
  id: string;
  name?: string;
  firstName?: string | null;
  lastName?: string | null;
  email: string;
  phone?: string | null;
  status?: string;
  avatarUrl?: string | null;
  roles?: unknown;
  personalCustomFields?: CustomFieldValueRow[];
  customFieldSections?: { groupName: string; fields: CustomFieldValueRow[] }[];
}

interface UserCustomFieldsFormData {
  personalFields: UserCustomFieldDefinition[];
  sections: { groupName: string; fields: UserCustomFieldDefinition[] }[];
  layout?: UserFormLayoutItem[];
}

function parseMultiselectValue(rawValue: string): string[] {
  try {
    const parsed = JSON.parse(rawValue) as unknown;
    if (Array.isArray(parsed)) return parsed.map((item) => String(item));
  } catch {
    /* comma-separated fallback */
  }
  return rawValue.split(',').map((part) => part.trim()).filter(Boolean);
}

function toDatetimeLocalValue(raw: string): string {
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return raw;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw.slice(0, 16);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function customFieldValuesFromUser(user?: UserDetail): Record<string, string | string[] | boolean> {
  const values: Record<string, string | string[] | boolean> = {};

  const assign = (field: CustomFieldValueRow) => {
    if (field.rawValue == null || field.rawValue === '') return;
    if (field.type === 'checkbox' || field.type === 'switch') {
      values[field.slug] = field.rawValue === '1' || field.rawValue === 'true';
      return;
    }
    if (field.type === 'multiselect') {
      values[field.slug] = parseMultiselectValue(field.rawValue);
      return;
    }
    if (field.type === 'file' || field.type === 'image') {
      const ids = parseCustomFieldMediaIds(field.rawValue);
      values[field.slug] = ids.length <= 1 ? (ids[0] ?? '') : ids;
      return;
    }
    if (field.type === 'datetime') {
      values[field.slug] = toDatetimeLocalValue(field.rawValue);
      return;
    }
    if (field.type === 'peso') {
      values[field.slug] = formatChileanPeso(field.rawValue);
      return;
    }
    values[field.slug] = field.rawValue;
  };

  for (const field of user?.personalCustomFields ?? []) assign(field);
  for (const section of user?.customFieldSections ?? []) {
    for (const field of section.fields) assign(field);
  }
  return values;
}

interface RoleOption {
  id: string;
  name: string;
  label?: string | null;
  hierarchyLevel?: number;
}

const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const LABEL = 'text-sm font-medium text-dash-on-surface-variant';

function SectionHeading({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="mb-6 flex items-center gap-2">
      <span className="material-symbols-outlined text-dash-primary" aria-hidden="true">
        {icon}
      </span>
      <h2 className="text-xl font-semibold text-dash-on-surface">{title}</h2>
    </div>
  );
}

export function UserFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canAssignRole, canManageUser, hasPermission } = usePermissions();
  const isEdit = Boolean(id);

  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFileName, setAvatarFileName] = useState('');
  const [courseSearch, setCourseSearch] = useState('');

  const { data: existing, isLoading } = useQuery({
    queryKey: ['users', id],
    queryFn: () => api<UserDetail>(`/users/${id}`),
    enabled: isEdit,
  });

  const existingRoles = useMemo(
    () => (existing ? extractUserRoleSlugs(existing.roles) : []),
    [existing],
  );
  const canEditUser = !isEdit || canManageUser(existingRoles);

  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => listResource<RoleOption>('/roles'),
  });

  const { data: customFieldsForm } = useQuery({
    queryKey: ['user-custom-fields', 'form'],
    queryFn: () => api<UserCustomFieldsFormData>('/user-custom-fields/form'),
  });

  const roles = useMemo(
    () => sortRolesByHierarchy(rolesData?.data ?? []).filter((role) => canAssignRole(role.name, role.hierarchyLevel)),
    [rolesData?.data, canAssignRole],
  );
  const personalCustomFields = useMemo(
    () => [...(customFieldsForm?.personalFields ?? [])].sort(compareCustomFieldOrder),
    [customFieldsForm?.personalFields],
  );
  const extraCustomFields = useMemo(
    () =>
      (customFieldsForm?.sections ?? [])
        .flatMap((section) =>
          section.fields.map((field) => ({ ...field, groupName: field.groupName || section.groupName })),
        )
        .sort(compareCustomFieldOrder),
    [customFieldsForm?.sections],
  );
  const customFieldById = useMemo(() => {
    const fields = new Map<string, UserCustomFieldDefinition>();
    for (const field of [...personalCustomFields, ...extraCustomFields]) fields.set(field.id, field);
    return fields;
  }, [personalCustomFields, extraCustomFields]);
  const layoutItems = useMemo(() => {
    if (customFieldsForm?.layout?.length) return customFieldsForm.layout;
    return fallbackUserFormLayout(
      personalCustomFields.map((field) => ({
        id: field.id,
        formWidth: customFieldUsesFullWidth(field) ? 'full' : 'half',
      })),
      extraCustomFields.map((field) => ({
        id: field.id,
        formWidth: customFieldUsesFullWidth(field) ? 'full' : 'half',
      })),
    );
  }, [customFieldsForm?.layout, personalCustomFields, extraCustomFields]);
  const extraGroups = useMemo(
    () =>
      layoutGroupsForExtra(
        layoutItems,
        (id) => customFieldById.get(id)?.groupName,
        (customFieldsForm?.sections ?? []).map((section) => section.groupName),
      ),
    [layoutItems, customFieldById, customFieldsForm?.sections],
  );

  const { register, handleSubmit, watch, setValue } = useForm<UserForm>({
    values: existing
      ? {
          firstName: existing.firstName ?? '',
          lastName: existing.lastName ?? '',
          email: existing.email,
          phone: existing.phone ?? '',
          status: existing.status ?? 'active',
          roles: extractUserRoleSlugs(existing.roles),
          customFields: customFieldValuesFromUser(existing),
        }
      : undefined,
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      status: 'active',
      roles: [],
      customFields: {},
      courseIds: [],
    },
  });

  const selectedRoles = watch('roles') ?? [];
  const selectedCourseIds = watch('courseIds') ?? [];
  const accountStatus = watch('status') ?? 'active';

  const canPickCourses = !isEdit && hasPermission(PERMISSIONS.CURSOS_VER);
  const { data: coursesData } = useQuery({
    queryKey: ['courses', 'user-create'],
    queryFn: () => listAllResource<CourseOption>('/courses', { perPage: 100 }),
    enabled: canPickCourses,
  });
  const publishedCourses = useMemo(
    () => (coursesData?.data ?? []).filter((course) => course.status === 'published'),
    [coursesData?.data],
  );
  const assignableCourses = useMemo(
    () => publishedCourses.filter((course) => matchesSearch(`${course.name} ${course.code ?? ''}`, courseSearch)),
    [publishedCourses, courseSearch],
  );

  const mutation = useMutation({
    mutationFn: (values: UserForm) => {
      if (!isEdit && values.password !== values.passwordConfirmation) {
        throw new Error('Las contraseñas no coinciden');
      }
      if (isEdit && values.password && values.password !== values.passwordConfirmation) {
        throw new Error('Las contraseñas no coinciden');
      }
      if (!isEdit && (!values.roles || values.roles.length === 0)) {
        throw new Error('Seleccione al menos un rol');
      }

      const assignableRoleNames = new Set(roles.map((role) => role.name));
      const selectedRoles = values.roles ?? [];
      if (selectedRoles.some((roleName) => !assignableRoleNames.has(roleName))) {
        throw new Error('No puede asignar uno o más de los roles seleccionados.');
      }

      const body = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        name: `${values.firstName.trim()} ${values.lastName.trim()}`.trim(),
        email: values.email.trim(),
        phone: values.phone?.trim() || undefined,
        status: values.status,
        roles: values.roles ?? [],
        customFields: values.customFields,
        ...(values.password ? { password: values.password } : {}),
        ...(!isEdit && values.status !== 'inactive' ? { courseIds: values.courseIds ?? [] } : {}),
      };
      return isEdit
        ? api(`/users/${id}`, { method: 'PATCH', body })
        : api('/users', { method: 'POST', body: { ...body, password: values.password! } });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      if (isEdit && id) {
        await queryClient.invalidateQueries({ queryKey: ['users', id] });
      }
      if (!isEdit) {
        await queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      }
      toast(userFormSuccessToast(isEdit));
      navigate(isEdit ? `/usuarios/${id}` : '/usuarios');
    },
    onError: (error) => {
      toast(userFormErrorToast(error));
    },
  });

  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(typeof ev.target?.result === 'string' ? ev.target.result : null);
    reader.readAsDataURL(file);
  }

  const previewUrl = avatarPreview ?? existing?.avatarUrl ?? null;

  function renderUserField(item: UserFormLayoutItem, wide: 'md' | 'lg') {
    const field = customFieldById.get(item.id);
    if (field) {
      return (
        <UserCustomFieldInput
          key={item.id}
          field={{ ...field, formWidth: item.formWidth }}
          register={register}
          setValue={setValue}
          watch={watch}
          className={customFieldFormSpan({ ...field, formWidth: item.formWidth }, wide)}
        />
      );
    }
    if (!isUserFormBuiltin(item.id)) return null;
    const span = formColumnSpan(item.formWidth, wide);
    switch (item.id) {
      case 'firstName':
        return (
          <div key={item.id} className={`space-y-2 ${span ?? ''}`}>
            <label htmlFor="first_name" className={LABEL}>
              Nombre <span className="text-error-600">*</span>
            </label>
            <input
              id="first_name"
              type="text"
              required
              placeholder="Ej: Juan"
              className={FIELD}
              {...register('firstName', { required: true })}
            />
          </div>
        );
      case 'lastName':
        return (
          <div key={item.id} className={`space-y-2 ${span ?? ''}`}>
            <label htmlFor="last_name" className={LABEL}>
              Apellidos <span className="text-error-600">*</span>
            </label>
            <input
              id="last_name"
              type="text"
              required
              placeholder="Ej: Pérez"
              className={FIELD}
              {...register('lastName', { required: true })}
            />
          </div>
        );
      case 'email':
        return (
          <div key={item.id} className={`space-y-2 ${span ?? ''}`}>
            <label htmlFor="email" className={LABEL}>
              Correo electrónico <span className="text-error-600">*</span>
            </label>
            <input
              id="email"
              type="email"
              required
              placeholder="juan.perez@academia.edu"
              className={FIELD}
              {...register('email', { required: true })}
            />
          </div>
        );
      case 'phone':
        return (
          <div key={item.id} className={`space-y-2 ${span ?? ''}`}>
            <label htmlFor="phone" className={LABEL}>
              Teléfono
            </label>
            <input id="phone" type="tel" placeholder="+56 9 1234 5678" className={FIELD} {...register('phone')} />
          </div>
        );
      case 'password':
        return (
          <div key={item.id} className={`space-y-2 ${span ?? ''}`}>
            <label htmlFor="password" className={LABEL}>
              {isEdit ? 'Nueva contraseña' : 'Contraseña'} {!isEdit && <span className="text-error-600">*</span>}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              className={FIELD}
              {...register('password', { required: !isEdit })}
            />
            {isEdit && (
              <p className="text-xs text-dash-on-surface-variant">Deje en blanco para mantener la contraseña actual.</p>
            )}
          </div>
        );
      case 'passwordConfirmation':
        return (
          <div key={item.id} className={`space-y-2 ${span ?? ''}`}>
            <label htmlFor="password_confirmation" className={LABEL}>
              {isEdit ? 'Confirmar nueva contraseña' : 'Confirmar contraseña'}{' '}
              {!isEdit && <span className="text-error-600">*</span>}
            </label>
            <input
              id="password_confirmation"
              type="password"
              autoComplete="new-password"
              className={FIELD}
              {...register('passwordConfirmation', { required: !isEdit })}
            />
          </div>
        );
      case 'status':
        return (
          <div key={item.id} className={`space-y-2 ${span ?? ''}`}>
            <label htmlFor="status" className={LABEL}>
              Estado
            </label>
            <select id="status" required className={FIELD} {...register('status')}>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>
        );
      case 'roles':
        return (
          <div key={item.id} className={`space-y-4 ${span ?? ''}`}>
            <span className={LABEL}>
              Roles asignados <span className="text-error-600">*</span>
            </span>
            <div className="flex flex-wrap gap-3">
              {roles.map((role) => {
                const checked = selectedRoles.includes(role.name);
                return (
                  <label
                    key={role.id}
                    className={`flex cursor-pointer items-center gap-2 rounded-full border border-dash-outline-variant px-4 py-2 transition-colors hover:bg-secondary-100 ${
                      checked ? 'border-dash-primary bg-brand-100 text-dash-primary' : ''
                    }`}
                  >
                    <input type="checkbox" className="sr-only" value={role.name} {...register('roles')} />
                    <span className="text-sm font-medium">
                      {roleLabel(role.name)} ({hierarchyLevelLabel(role.hierarchyLevel ?? roleHierarchyLevel(role.name))})
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      default:
        return null;
    }
  }

  function renderLayoutSection(
    section: UserFormSectionId,
    icon: string,
    title: string,
    gridClass: string,
    wide: 'md' | 'lg',
  ) {
    const sectionItems = layoutItemsForSection(layoutItems, section);
    if (sectionItems.length === 0) return null;
    return (
      <section className="border-b border-dash-outline-variant p-6 md:p-8">
        <SectionHeading icon={icon} title={title} />
        <div className={gridClass}>{sectionItems.map((item) => renderUserField(item, wide))}</div>
      </section>
    );
  }

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink
          to={isEdit ? `/usuarios/${id}` : '/usuarios'}
          label={isEdit ? 'Volver al detalle del usuario' : 'Volver a gestión de usuarios'}
        />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
            {isEdit ? 'Editar usuario' : 'Nuevo usuario'}
          </h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            {isEdit ? 'Actualizar los datos del usuario en el sistema' : 'Crear un nuevo usuario en el sistema'}
          </p>
        </div>
      </div>

      {isEdit && !isLoading && !canEditUser && (
        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert">
          No puede editar este usuario: su nivel jerárquico no es superior al del usuario seleccionado.
        </p>
      )}

      {isEdit && isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <div className="lms-credentials-glass-panel overflow-hidden rounded-xl shadow-sm">
          <form
            onSubmit={handleSubmit((values) => {
              if (!canEditUser) return;
              mutation.mutate(values);
            })}
          >
            <fieldset disabled={!canEditUser} className="contents">
            {renderLayoutSection('personal', 'person', 'Información Personal', 'grid grid-cols-1 gap-6 md:grid-cols-2', 'md')}
            {renderLayoutSection('security', 'lock', 'Seguridad', 'grid grid-cols-1 gap-6 md:grid-cols-2', 'md')}
            {renderLayoutSection(
              'account',
              'settings_account_box',
              'Configuración de Cuenta',
              'grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12',
              'lg',
            )}
            {extraGroups.map((group) => (
              <section key={group.groupName} className="border-b border-dash-outline-variant p-6 md:p-8">
                <SectionHeading icon={formGroupIcon(group.groupName)} title={group.groupName} />
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {group.items.map((item) => renderUserField(item, 'lg'))}
                </div>
              </section>
            ))}
            {!isEdit && canPickCourses && (
              <section className="border-b border-dash-outline-variant p-6 md:p-8">
                <SectionHeading icon="school" title="Cursos" />
                <p className="mb-4 text-sm text-dash-on-surface-variant">
                  Puede matricular al usuario en uno o más cursos publicados. Si el estado es inactivo, la asignación se omite.
                </p>
                <div className="mb-3">
                  <SearchInput
                    value={courseSearch}
                    onChange={setCourseSearch}
                    placeholder="Buscar por nombre o código"
                    aria-label="Buscar por nombre o código"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-dash-outline-variant bg-white p-1">
                  {publishedCourses.length === 0 ? (
                    <p className="px-3 py-6 text-center text-sm text-dash-on-surface-variant">
                      No hay cursos publicados para asignar.
                    </p>
                  ) : assignableCourses.length === 0 ? (
                    <SearchEmptyState term={courseSearch.trim()} onClear={() => setCourseSearch('')} />
                  ) : (
                    assignableCourses.map((course) => {
                      const checked = selectedCourseIds.includes(course.id);
                      return (
                        <label
                          key={course.id}
                          className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-secondary-50 ${
                            checked ? 'bg-brand-50' : ''
                          } ${accountStatus === 'inactive' ? 'pointer-events-none opacity-50' : ''}`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-dash-outline-variant text-dash-primary"
                            value={course.id}
                            disabled={accountStatus === 'inactive'}
                            {...register('courseIds')}
                          />
                          <span className="min-w-0 text-sm text-dash-on-surface">
                            {course.name}
                            {course.code ? <span className="text-dash-on-surface-variant"> · {course.code}</span> : null}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
                <p className="mt-2 text-xs text-dash-on-surface-variant">
                  {selectedCourseIds.length === 0
                    ? 'Sin cursos seleccionados'
                    : `${selectedCourseIds.length} ${selectedCourseIds.length === 1 ? 'curso seleccionado' : 'cursos seleccionados'}`}
                </p>
              </section>
            )}
            <section className="border-b border-dash-outline-variant p-6 md:p-8">
              <SectionHeading icon="add_a_photo" title="Foto de perfil" />
              <div className="flex flex-col items-center gap-8 md:flex-row">
                <div
                  className={`flex h-32 w-32 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-dash-outline-variant bg-secondary-100 ${
                    previewUrl ? 'border-solid' : ''
                  }`}
                >
                  {previewUrl ? (
                    <img src={previewUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="material-symbols-outlined text-4xl text-dash-on-surface-variant" aria-hidden="true">
                      account_circle
                    </span>
                  )}
                </div>
                <div className="w-full flex-1">
                  <label className="group flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-dash-outline-variant bg-secondary-50 p-8 text-center transition-colors hover:border-dash-primary">
                    <input
                      type="file"
                      name="avatar"
                      accept="image/*"
                      className="sr-only"
                      onChange={handleAvatarChange}
                    />
                    <span
                      className="material-symbols-outlined mb-2 text-3xl text-dash-primary transition-transform group-hover:scale-110"
                      aria-hidden="true"
                    >
                      cloud_upload
                    </span>
                    <p className="mb-1 text-sm font-medium text-dash-on-surface">Haga clic para subir o arrastre y suelte</p>
                    <p className="text-sm text-dash-on-surface-variant">PNG, JPG o WEBP (máx. 2MB)</p>
                    {avatarFileName && <p className="mt-2 text-xs text-dash-secondary">{avatarFileName}</p>}
                  </label>
                  {isEdit && existing?.avatarUrl && (
                    <div className="mt-4">
                      <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-dash-on-surface-variant">
                        <input
                          type="checkbox"
                          name="remove_avatar"
                          value="1"
                          className="rounded border-dash-outline-variant text-dash-primary focus:ring-dash-secondary/20"
                        />
                        Eliminar foto actual
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <footer className="flex flex-col justify-end gap-4 bg-secondary-50/80 p-6 sm:flex-row md:p-8">
              <Link
                to={isEdit ? `/usuarios/${id}` : '/usuarios'}
                className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant px-8 py-3 text-sm font-medium text-dash-primary transition-colors hover:bg-secondary-100"
              >
                Cancelar
              </Link>
              <button
                type="submit"
                disabled={mutation.isPending || !canEditUser}
                className="inline-flex items-center justify-center rounded-lg bg-dash-primary px-8 py-3 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
              >
                {mutation.isPending ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear usuario'}
              </button>
            </footer>
            </fieldset>
          </form>
        </div>
      )}
    </div>
  );
}
